import React, { useContext, useState, lazy, Suspense } from "react";
import friendlyTime from 'friendly-time';
import {Card, CardActionArea, CardContent, CardMedia, Typography, Grid, Stack, Box, IconButton } from '@mui/material';
import { cloudflareCDN } from "../../libs/util";
import { getUrlFromRef } from "../../libs/firebase";
import { ExistingCardMoreOptions } from "./ExistingCardMoreOptions";
import { CardTypes } from "ts-interfaces";
import { ProjectSchemeInformation } from "./../projects_scheme/ProjectSchemeInformation";
import { AuthContext } from "../../auth/Auth";
import { useTeamsProAccess } from "../../hooks/useTeamsProAccess";

const ShareResourceModal = lazy(() => import("./../share_resource").then(module => ({ default: module.ShareResourceModal })));

const ExistingCardComponent = ({ 
    data, 
    type, 
    designFirms, 
    teamMembers, 
    onClick, 
    onEdit=()=>{}, 
    onDelete, 
    onCopy=(_)=>{}, 
    onRename=(_)=>{}, 
    onAssociatedProjectChange=(_)=>{}, 
    onShare=(_)=>{}, 
    onRemoveFromProject=(_)=>{}, 
    cardSize="default",
    hasViewerAccess = false,
    isDisabled = false,
    coverImage = null
}: {
    data: any;
    type: any;
    designFirms: any;
    teamMembers: any;
    onClick: any;
    onEdit?: () => void;
    onDelete: any;
    onCopy?: (_: any) => void;
    onRename?: (_: any) => void;
    onAssociatedProjectChange?: (_: any) => void;
    onShare?: (_: any) => void;
    onRemoveFromProject?: (_: any) => void;
    cardSize?: string;
    hasViewerAccess?: boolean;
    isDisabled?: boolean;
    coverImage?: string | null;
}) => {
    const {currentUser} = useContext(AuthContext);
    const [showShareModal, setShowShareModal] = useState(false);
    const { isTeamsProUser, isProUser } = useTeamsProAccess();  
    const hasProjectInfo = type === CardTypes.Projects;
    const cardCss = cardSize == 'small' ? { width: '150px', height: 'auto', boxShadow: 'none' } : { width: '385px', height: '270px', borderRadius:'10px' };
    const mediaCss = cardSize == 'small' ? { height: 150 } : { height: 196 };
    const titleFontCss = cardSize == 'small' ? { fontSize: '0.9rem' } : { };
    const subtitleFontCss = cardSize == 'small' ? { fontSize: '0.65rem' } : { };
    const borderRadiusCss = cardSize == 'small' ? { borderRadius: '10px' } : {};
    const paddingCss = cardSize == 'small' ? { padding: '4px' } : { padding: '16px' };
    const ellipsisText = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block'
    };

    return (
    <>
        <Card style={cardCss} onClick={() => !isDisabled && onClick(null)}>
            <CardActionArea disableTouchRipple={true} disableRipple={true} component="div">
                {type == CardTypes.Boards &&
                    <CardMedia
                        sx={{
                            ...mediaCss, 
                            ...borderRadiusCss, 
                            backgroundColor: '#f0f0f0',
                            ...(isDisabled && {
                                opacity: 0.5,
                                filter: 'grayscale(50%)'
                            })
                        }}
                        image={coverImage || cloudflareCDN(getUrlFromRef(data.thumbnail),'height=220,format=auto')}
                        title={data.name || "Untitled"}
                    />
                }
                {type == CardTypes.Projects &&
                    <Stack 
                        direction="row" 
                        spacing={1} 
                        sx={{ 
                            padding: '8px', 
                            paddingTop: '12px',
                            ...(isDisabled && {
                                opacity: 0.5,
                                filter: 'grayscale(50%)'
                            })
                        }}
                    >
                        {coverImage ? (
                            // If coverImage is provided, use it as a single large image
                            <Stack
                                sx={{
                                    width: '100%',
                                    height: '120px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    backgroundColor: '#f0f0f0',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0'
                                }}
                            >
                                <Box
                                    component="img"
                                    src={coverImage}
                                    alt={data.name || "Untitled"}
                                    sx={{
                                        width: '100%',
                                        height: '120px',
                                        objectFit: 'cover',
                                        borderRadius: '6px'
                                    }}
                                />
                            </Stack>
                        ) : Array.isArray(data.boards) && data.boards.length > 0 ? (
                            // Fall back to existing board thumbnail logic
                            Array.from({ length: 3 }).map((_, index) => (
                                <Stack
                                    key={index}
                                    sx={{
                                        width: '120px',
                                        height: '120px',
                                        flex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0'
                                    }}
                                >
                                    {data.boards?.[index]?.files?.screenshot ? (
                                        <Box
                                            component="img"
                                            src={cloudflareCDN(getUrlFromRef(data.boards[index].files?.screenshot), 'height=220,format=auto')}
                                            alt={data.name || "Untitled"}
                                            sx={{
                                                width: '120px',
                                                height: '120px',
                                                objectFit: 'cover',
                                                borderRadius: '6px'
                                            }}
                                        />
                                    ) : null}
                                </Stack>
                            ))
                        ) : (
                            // No boards and no coverImage
                            <Stack
                                sx={{
                                    width: '120px',
                                    height: '120px',
                                    flex: 1,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '8px',
                                    border: '1px solid #e0e0e0'
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    No Boards Yet
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                }
                {type == CardTypes.Collections && cardSize !== 'small' &&
                    <Stack 
                        direction="row" 
                        spacing={0}
                        sx={{
                            ...(isDisabled && {
                                opacity: 0.5,
                                filter: 'grayscale(50%)'
                            })
                        }}
                    >
                        {coverImage ? (
                            // If coverImage is provided, use it as a single image spanning the full width
                            <CardMedia
                                style={{ ...mediaCss, width: '100%' }}
                                image={coverImage}
                                title={data.name || "Untitled"}
                            />
                        ) : (
                            // Fall back to existing thumbnail logic
                            Array.from({ length: 3 }).map((_, index) => (
                                data.thumbnail?.[index]?.src ? (
                                    <CardMedia
                                        key={index}
                                        style={{ ...mediaCss, flex: 1 }}
                                        image={data.thumbnail[index].src}
                                        title={data.name || "Untitled"}
                                    />
                                ) : (
                                    <Grid
                                        key={index}
                                        style={{
                                            ...mediaCss,
                                            flex: 1,
                                            backgroundColor: '#f0f0f0', // Light gray placeholder
                                        }}
                                    />
                                )
                            ))
                        )}
                    </Stack>
                }
                {type == CardTypes.Collections && cardSize == 'small' &&
                    <Stack 
                        direction="row" 
                        spacing={.2} 
                        sx={{ 
                            ...mediaCss, 
                            p: 0.5,
                            ...(isDisabled && {
                                opacity: 0.5,
                                filter: 'grayscale(50%)'
                            })
                        }}
                    >
                        {coverImage ? (
                            // If coverImage is provided, use it as a single image spanning the full area
                            <CardMedia
                                style={{ 
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '10px'
                                }}
                                image={coverImage}
                                title={data.name || "Untitled"}
                            />
                        ) : (
                            // Fall back to existing thumbnail layout logic
                            <>
                                {/* Thumbnail 1 - 60% width, 100% height */}
                                <Grid sx={{ width: '60%', height: '100%' }}>
                                    {data.thumbnail?.[0]?.src ? (
                                        <CardMedia
                                            style={{ 
                                                height: '100%',
                                                borderTopLeftRadius: '10px',
                                                borderBottomLeftRadius: '10px'
                                            }}
                                            image={data.thumbnail[0].src}
                                            title={data.name || "Untitled"}
                                        />
                                    ) : (
                                        <Grid
                                            sx={{
                                                height: '100%',
                                                backgroundColor: '#f0f0f0',
                                                borderTopLeftRadius: '10px',
                                                borderBottomLeftRadius: '10px'
                                            }}
                                        />
                                    )}
                                </Grid>
                                
                                {/* Right column - 40% width containing thumbnails 2 and 3 stacked */}
                                <Stack direction="column" spacing={.2} sx={{ width: '40%', height: '100%' }}>
                                    {/* Thumbnail 2 - 100% width, 50% height within the right column */}
                                    <Grid sx={{ height: '50%' }}>
                                        {data.thumbnail?.[1]?.src ? (
                                            <CardMedia
                                                style={{ 
                                                    height: '100%',
                                                    borderTopRightRadius: '10px'
                                                }}
                                                image={data.thumbnail[1].src}
                                                title={data.name || "Untitled"}
                                            />
                                        ) : (
                                            <Grid
                                                sx={{
                                                    height: '100%',
                                                    backgroundColor: '#f0f0f0',
                                                    borderTopRightRadius: '10px'
                                                }}
                                            />
                                        )}
                                    </Grid>
                                    
                                    {/* Thumbnail 3 - 100% width, 50% height within the right column */}
                                    <Grid sx={{ height: '50%' }}>
                                        {data.thumbnail?.[2]?.src ? (
                                            <CardMedia
                                                style={{ 
                                                    height: '100%',
                                                    borderBottomRightRadius: '10px'
                                                }}
                                                image={data.thumbnail[2].src}
                                                title={data.name || "Untitled"}
                                            />
                                        ) : (
                                            <Grid
                                                sx={{
                                                    height: '100%',
                                                    backgroundColor: '#f0f0f0',
                                                    borderBottomRightRadius: '10px'
                                                }}
                                            />
                                        )}
                                    </Grid>
                                </Stack>
                            </>
                        )}
                    </Stack>
                }
                <CardContent sx={{...paddingCss }}>
                    {type === CardTypes.Projects ? (
                        <Stack spacing={1} sx={{ paddingBottom: '2px' }}>
                            <Stack
                                sx={{
                                    ...(isDisabled && {
                                        opacity: 0.5,
                                        filter: 'grayscale(50%)'
                                    })
                                }}
                            >
                                <Typography variant="h6" component="h2" sx={{...ellipsisText, fontWeight: 600}}>
                                    {data.name || "Untitled"}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" component="span" sx={{...ellipsisText}}>
                                    <b>{ data.boards?.length ? data.boards.length : 0 } Boards</b>&nbsp;&nbsp;Updated { friendlyTime( new Date(data.updatedAt)) }
                                </Typography>
                            </Stack>
                            
                            {/* Action bar on separate line for projects */}
                            <Stack 
                                direction="row" 
                                spacing={0}
                                sx={{ 
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    paddingTop: '4px',
                                }}
                                onClick={(e) => e.stopPropagation()}>

                                {!hasViewerAccess && isTeamsProUser &&
                                    <Suspense fallback={null}>
                                        <ShareResourceModal title={data.name} thumbnail={data.thumbnail} resourceID={data.id} type={type} owner={data.owner || data.user || data.uid} />
                                    </Suspense>
                                }   
                                
                                {hasProjectInfo && 
                                    <ProjectSchemeInformation projectData={data} />
                                }

                                {!hasViewerAccess &&
                                    <ExistingCardMoreOptions 
                                        type={type}
                                        onEdit={onEdit}
                                        onDelete={onDelete} 
                                        onCopy={onCopy} 
                                        onRename={onRename} 
                                        onAssociatedProjectChange={onAssociatedProjectChange} 
                                        onShare={onShare}
                                        cardSize={cardSize}
                                        onRemoveFromProject={onRemoveFromProject}
                                        isTeamsProUser={isTeamsProUser || false}
                                        isProUser={isProUser || false}
                                        disabled={isDisabled}
                                    />
                                }
                            </Stack>
                        </Stack>
                    ) : (
                    <Stack 
                        direction="row"
                        spacing={1}
                        sx={{ width: '100%' }}
                    >
                        <Stack 
                            sx={{ 
                                flex: 7, 
                                minWidth: 0,
                                ...(isDisabled && {
                                    opacity: 0.5,
                                    filter: 'grayscale(50%)'
                                })
                            }}
                        >
                            <Typography variant="h6" component="h2" sx={{ ...ellipsisText, fontWeight: 600, ...titleFontCss }}>
                                {data.name ? data.name.charAt(0).toUpperCase() + data.name.slice(1) : "Untitled"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" component="p" sx={{...ellipsisText, ...subtitleFontCss}}>
                                Updated { friendlyTime( new Date(data.updatedAt)) }
                            </Typography>
                        </Stack>
                        
                        {/* Action bar - takes full width on mobile, 5/12 on larger screens */}
                        {!hasViewerAccess && 
                            <Stack 
                                direction="row" 
                                spacing={0}
                                sx={{ 
                                    flexShrink: 0,
                                    justifyContent: 'flex-end',
                                    alignItems: 'center'
                                }}
                                onClick={(e) => e.stopPropagation()}>

                                {isTeamsProUser && cardSize !== 'small' && (
                                    <Suspense fallback={null}>
                                        <ShareResourceModal title={data.name} thumbnail={data.thumbnail} resourceID={data.id} type={type} owner={data.owner || data.user || data.uid} />
                                    </Suspense>
                                )}

                                <ExistingCardMoreOptions 
                                    type={type}
                                    onEdit={onEdit}
                                    onDelete={onDelete} 
                                    onCopy={onCopy} 
                                    onRename={onRename} 
                                    onAssociatedProjectChange={onAssociatedProjectChange} 
                                    onShare={onShare}
                                    cardSize={cardSize}
                                    onRemoveFromProject={onRemoveFromProject}
                                    isTeamsProUser={isTeamsProUser || false}
                                    isProUser={isProUser || false}
                                    disabled={isDisabled}
                                />
                            </Stack>
                        }
                    </Stack>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    </>
    );
}

export const ExistingCard = React.memo(ExistingCardComponent);